import { isMatchStarted } from '@/stores/matchStore';
import type { Match, MatchReactionType } from '@/types';

export interface MatchReactionDef {
  id: MatchReactionType;
  emoji: string;
  label: string;
}

export const MATCH_REACTION_DEFS: MatchReactionDef[] = [
  { id: 'force', emoji: '💪', label: 'Force' },
  { id: 'like', emoji: '👍', label: "J'aime" },
  { id: 'dislike', emoji: '👎', label: "Je n'aime pas" },
  { id: 'fire', emoji: '🔥', label: 'En feu' },
];

const PRE_MATCH_REACTIONS: MatchReactionType[] = ['force'];
const LIVE_REACTIONS: MatchReactionType[] = ['like', 'dislike', 'force', 'fire'];

const REACTION_DEF_MAP = new Map(MATCH_REACTION_DEFS.map((d) => [d.id, d]));

export interface MatchReactionToolbarItem extends MatchReactionDef {
  enabled: boolean;
  phaseHint?: string;
}

export function getMatchReactionDef(id: MatchReactionType): MatchReactionDef | undefined {
  return REACTION_DEF_MAP.get(id);
}

function enabledReactionIds(match: Match, at: Date): MatchReactionType[] {
  return isMatchStarted(match, at) ? LIVE_REACTIONS : PRE_MATCH_REACTIONS;
}

/** Toutes les réactions pour la barre d’actions ; `enabled` selon la phase du match. */
export function getMatchReactionToolbarItems(match: Match, at: Date = new Date()): MatchReactionToolbarItem[] {
  const started = isMatchStarted(match, at);
  const enabledSet = new Set(enabledReactionIds(match, at));
  return MATCH_REACTION_DEFS.map((def) => {
    const enabled = enabledSet.has(def.id);
    return {
      ...def,
      enabled,
      phaseHint: enabled
        ? undefined
        : started
          ? "Indisponible après le coup d'envoi."
          : "Disponible au coup d'envoi.",
    };
  });
}

/** @deprecated Préférer getMatchReactionToolbarItems */
export function getAvailableMatchReactions(match: Match, at: Date = new Date()): MatchReactionDef[] {
  const enabled = new Set(enabledReactionIds(match, at));
  return MATCH_REACTION_DEFS.filter((d) => enabled.has(d.id));
}

export function isMatchReactionAvailable(
  match: Match,
  reaction: MatchReactionType,
  at: Date = new Date(),
): boolean {
  return enabledReactionIds(match, at).includes(reaction);
}

/** Like et dislike sont exclusifs : on ne peut pas avoir les deux en même temps. */
export function getExclusiveMatchReaction(reaction: MatchReactionType): MatchReactionType | null {
  if (reaction === 'like') return 'dislike';
  if (reaction === 'dislike') return 'like';
  return null;
}

export function emptyMatchReactionCounts(): Record<MatchReactionType, number> {
  return {
    going: 0,
    force: 0,
    like: 0,
    dislike: 0,
    fire: 0,
  };
}
